import { createReducer, on } from '@ngrx/store';
import { closingDocumentActions } from '../actions/closing-document.actions';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import type { RequestStatus } from './project-accounts.reducer';
import { ISharepointFetchResponse } from '../models/closing-document.model';
import {
    DocumentFilterCounts,
    DocumentFilterKey,
    DocumentSort,
} from '../components/documents-hub/documents-hub.types';
import {
    IClosingAccountOption,
    IClosingDocumentDetail,
    IClosingDocumentListItem,
    ISyncRun,
    IUploadFailure,
    SyncStartRefusal,
} from '../models/closing-document.model';

export interface ClosingDocumentState {
    /**
     * Which closing this slice is about. Real state, not effect bookkeeping: the reload effects need
     * it (a filter change or an upload has to re-request the SAME project), and holding it here keeps
     * the effects stateless. An effect instance field would survive across route changes and quietly
     * reload the wrong project.
     */
    projectId: string | null;
    /** A SharePoint pull is in flight — the hub disables its sync control while it is. */
    syncPending: boolean;
    /**
     * The last pull's outcome, so the UI can distinguish "imported 3" from "nothing new".
     * `null` until the user syncs once in this session.
     */
    lastSync: ISharepointFetchResponse | null;

    // --- REV-21964 - R3/R6/U1: der asynchrone Lauf und sein Bericht ---------------------------
    /**
     * Der Lauf, dem gerade gefolgt wird - `null`, wenn keiner laeuft.
     *
     * Getrennt von `syncRun.id`, weil die beiden zu verschiedenen Zeitpunkten existieren: die Id
     * kommt mit der Startantwort, der erste Bericht erst eine Abfrage spaeter. Ohne dieses Feld
     * haette das Nachfragen in genau dem Moment nichts zu fragen, in dem es beginnen muss.
     */
    syncRunId: string | null;
    /**
     * U1 - der Bericht des letzten Laufs, vom SERVER gelesen und nicht vom Reiter gemerkt.
     *
     * Genau das ist der Unterschied zu REV-21376: dort stand dieselbe Auskunft in der
     * Fetch-Antwort und wurde nirgends abgelegt, also hatte "warum fehlt mein Dokument?" eine
     * Antwort, solange der Reiter offen blieb - und gefragt wird das nach der Rueckkehr.
     */
    syncRun: ISyncRun | null;
    /**
     * R3/R6 - warum der letzte Start abgewiesen wurde, oder `null`.
     *
     * KEIN Fehlerfeld. Alle drei Abweisungen kommen mit HTTP 200 und sagen etwas Wahres ueber die
     * Abrechnung; `error` bedeutet in diesem Slice "die Liste konnte nicht geladen werden" und
     * nimmt dem Nutzer die Tabelle weg (F5). Eine Auskunft darf das nicht.
     */
    syncRefusal: SyncStartRefusal | null;
    /**
     * Wie oft schon nach dem Bericht gefragt wurde - die Decke des Nachfragens, in TICKS statt in
     * Millisekunden, aus demselben Grund wie `uploadPollTick`: die Kadenz ist injiziert und darf
     * kurz sein, die Decke soll davon unabhaengig bleiben.
     *
     * Ohne Decke fragt ein Lauf, der serverseitig haengen bleibt, bis zum Seitenwechsel weiter -
     * genau die unbegrenzte Schleife, die #162/SRR F3 fuer den anderen Poll abgestellt hat.
     */
    syncPollTick: number;
    documents: IClosingDocumentListItem[];
    /** From the server — see the model. Never derived from `documents.length`. */
    counts: DocumentFilterCounts;
    activeFilter: DocumentFilterKey;
    sort: DocumentSort | null;
    searchTerm: string;
    /**
     * REV-21901 N3/Weg 1 (W2) — the "automatisch zugewiesen" facet. In the SLICE like `activeFilter`,
     * not page-local: the facet is a server parameter (`assignedBy=agent`), and every reload path
     * that reads what is on screen from the store (remount, SharePoint modal) must carry it — a
     * page-local flag would be silently un-narrowed by exactly those paths. Cleared by
     * `forNewProject` like everything else that belongs to one closing.
     */
    agentOnly: boolean;
    loading: boolean;
    error: boolean;

    /**
     * F5 (#84) — the write-failure state of accept/reject/un-file, SEPARATE from `error`/
     * `documentError`. `error` means "the list/document could not be LOADED" and both surfaces show
     * it with precedence over their content; a failed WRITE must not take the surface away from the
     * user who is looking straight at it.
     *
     * ⚠️ F9/F10 (Sicherheitspruefung, Fall 18) — these are SETS (`string[]`), not a single
     * `string | null` slot. A slot was a real double-write path: accept A (slot holds A) → accept B
     * (slot now holds B, A reads as "not pending" even though A's write is still in flight) → accept
     * A again ⇒ a SECOND POST for A, which `mergeMap`/`exhaustMap` correctly does not discard because
     * nothing told it the first one was still running. The hub can have MANY rows in flight or failed
     * at once, and a set is the only shape that says which ones without one overwriting another.
     */
    proposalPendingIds: string[];
    proposalFailedIds: string[];

    /**
     * REV-22366 — rows with a write running (Nicht relevant, choosing an account), in two steps: the
     * write itself (`rowWritesRunning`), then the list that shows its result (`rowWritesAwaitingList`).
     * A list that lands while the write still runs (a poll) does not end it, so the old state is never
     * shown as if it were the result. The reload after the write cancels such a poll (`switchMap`).
     */
    rowWritesRunning: string[];
    rowWritesAwaitingList: string[];

    /**
     * The documents whose last relevance, filing or validity write failed, for the message
     * on the document page. Proposal writes keep their own set (`proposalFailedIds`).
     */
    documentWriteFailedIds: string[];

    /**
     * REV-22366 — the files of a hub upload, shown as placeholder rows from the click until the list
     * holds the documents. The upload runs with the modal already closed, so without them nothing would
     * show that it is happening. `uploadAwaitingList`: the upload settled, the next list ends them.
     */
    uploadingFilenames: string[];
    uploadAwaitingList: boolean;

    /** The single-document route. */
    document: IClosingDocumentDetail | null;
    documentLoading: boolean;
    documentError: boolean;

    /** Fed straight into the upload modal's `[pending]` / `[failures]`. */
    uploadPending: boolean;
    uploadFailures: IUploadFailure[];

    /**
     * The post-upload poll's watch-list (#162, AP8a) — filenames from the MOST RECENT upload batch
     * that are still `open`, i.e. still waiting for a proposal. Empty means "no poll running": the
     * effect (`pollAfterUpload$`) only continues the chain while this is non-empty, so clearing it (a
     * proposal arrived for every one of them, or the tick ceiling was reached) is what ends the chain.
     */
    pendingUploadFilenames: string[];
    /** How many `Load Documents` requests this watch has already spent, against the injected ceiling. */
    uploadPollTick: number;

    /**
     * The account plan both pickers list. Loaded once per closing.
     *
     * Empty is a legitimate state and renders the pickers' own `documents-picker-empty` — which is
     * exactly what the whole ticket showed until the assignment landed, when `[accounts]` was fed a
     * hardcoded `[]`.
     */
    accounts: IClosingAccountOption[];

    /**
     * How far the account-plan read has got — `'loaded'` is what lets the plan be served from the
     * store instead of refetched.
     *
     * A STATUS and not two booleans, matching `project-accounts.reducer.ts`: with `loading` and
     * `error` alone, "never asked" and "finished" are the same pair of `false`s, and a cache cannot
     * be built on a value that cannot tell them apart.
     */
    accountsStatus: RequestStatus;

    /**
     * The documents of the account currently open — SEPARATE from `documents`, which belongs to the
     * hub's active pill. One array for both would make a visit to an account page change what the
     * hub shows.
     */
    accountDocuments: IClosingDocumentListItem[];
    accountDocumentsLoading: boolean;
    accountDocumentsError: boolean;
    /**
     * The account whose documents were asked for LAST. An upload that settles after the user moved on
     * reloads its account only while that account is still the one asked for (`uploadDocuments$`).
     */
    accountDocumentsAccountId: string | null;
}

export const initialClosingDocumentState: ClosingDocumentState = {
    projectId: null,
    documents: [],
    counts: { inbox: 0, assigned: 0, notRelevant: 0 },
    // Matches the hub's own default AND the server's default `filter` — so the first paint, the first
    // request and the pill that is marked active all agree without anyone coordinating.
    activeFilter: 'inbox',
    sort: null,
    searchTerm: '',
    agentOnly: false,
    loading: false,
    error: false,
    proposalPendingIds: [],
    proposalFailedIds: [],
    rowWritesRunning: [],
    rowWritesAwaitingList: [],
    documentWriteFailedIds: [],
    uploadingFilenames: [],
    uploadAwaitingList: false,
    document: null,
    documentLoading: false,
    documentError: false,
    uploadPending: false,
    uploadFailures: [],
    pendingUploadFilenames: [],
    uploadPollTick: 0,
    syncPending: false,
    lastSync: null,
    syncRunId: null,
    syncRun: null,
    syncRefusal: null,
    syncPollTick: 0,
    accounts: [],
    accountsStatus: 'idle',
    accountDocuments: [],
    accountDocumentsLoading: false,
    accountDocumentsError: false,
    accountDocumentsAccountId: null,
};

/**
 * Everything that belongs to ONE closing, cleared when the slice moves to another.
 *
 * ONE helper because EVERY action that names a closing has to do this — the same construction, and
 * for the same reason, as `forNewProject` in `project-accounts.reducer.ts`. That slice learned it the
 * hard way: the reset lived inline in a single action, and every OTHER way into a closing carried the
 * previous one's state across. This slice had the identical shape until 23.08.2026 — the reset sat on
 * `loadDocuments` alone, so a cold deep link to `…/documents/:documentId` of another closing (a
 * bookmark, a shared link, F5 on the page) never triggered it: that route dispatches `loadDocument`
 * and `loadAccounts` and never `loadDocuments`, so `projectId` kept naming the OLD closing while
 * `accounts` and `document` were overwritten with the new one's.
 *
 * Rebuilt from the initial state rather than field by field, so a field added later is covered
 * without anyone remembering this function. Returns `{}` for the closing already in state, which is
 * the normal case — a reducer can spread it unconditionally and set its own fields after.
 */
/** Add `id` to `ids`, deduplicated — a document already pending/failed stays a single entry. */
function withId(ids: string[], id: string): string[] {
    return ids.includes(id) ? ids : [...ids, id];
}

/** Remove `id` from `ids`, or return the SAME array reference if it was never in it. */
function withoutId(ids: string[], id: string): string[] {
    return ids.includes(id) ? ids.filter((existing) => existing !== id) : ids;
}

function forNewProject(state: ClosingDocumentState, projectId: string): Partial<ClosingDocumentState> {
    if (state.projectId === projectId) {
        return {};
    }

    // Why each of these must go: `accounts` would offer the previous chart in the picker, `lastSync`
    // would report a pull that never touched this closing, `accountDocuments` would list another
    // closing's evidence, and a `searchTerm` carried over narrows the counts for a reason the user
    // cannot see.
    return { ...initialClosingDocumentState };
}

export const closingDocumentReducer = createReducer(
    initialClosingDocumentState,
    // `error` is cleared on every new load: a retry that succeeds must not keep showing the old
    // error, and the hub renders error with precedence over rows.
    on(closingDocumentActions.loadDocuments, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        loading: true,
        error: false,
        // Counts a tick of the post-upload poll (#162) ONLY while it is actually watching something —
        // every OTHER caller of `Load Documents` (a pill click, a search) is not the poll, and must not
        // spend its ceiling.
        ...(state.pendingUploadFilenames.length > 0 ? { uploadPollTick: state.uploadPollTick + 1 } : {}),
    })),
    // The hub's own refresh. It carries the closing because on the very first load the slice does not
    // know it yet — so it is an entry point like the others and resets like them.
    on(closingDocumentActions.reloadDocuments, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        loading: true,
        error: false,
        // Opening the tab is the user's step — see `Set Filter` for why the rows go.
        documents: [],
    })),
    on(closingDocumentActions.loadDocumentsSuccess, (state, { response }) => ({
        ...state,
        documents: response.documents,
        counts: response.counts,
        loading: false,
        error: false,
        rowWritesAwaitingList: [],
        ...(state.uploadAwaitingList ? { uploadingFilenames: [], uploadAwaitingList: false } : {}),
    })),
    // Validity is a write on the same document too, so the document page can show it running.
    on(
        closingDocumentActions.setNotRelevant,
        closingDocumentActions.assignDocument,
        closingDocumentActions.setDocumentValidity,
        (state, { documentId }) => ({
            ...state,
            rowWritesRunning: withId(state.rowWritesRunning, documentId),
            documentWriteFailedIds: withoutId(state.documentWriteFailedIds, documentId),
        }),
    ),
    on(closingDocumentActions.rowWriteSucceeded, (state, { documentId }) => ({
        ...state,
        rowWritesRunning: withoutId(state.rowWritesRunning, documentId),
        rowWritesAwaitingList: withId(state.rowWritesAwaitingList, documentId),
    })),
    on(closingDocumentActions.documentWriteSucceeded, (state, { documentId }) => ({
        ...state,
        rowWritesRunning: withoutId(state.rowWritesRunning, documentId),
    })),
    on(closingDocumentActions.rowWriteFailed, (state, { documentId }) => ({
        ...state,
        rowWritesRunning: withoutId(state.rowWritesRunning, documentId),
        rowWritesAwaitingList: withoutId(state.rowWritesAwaitingList, documentId),
        documentWriteFailedIds: withId(state.documentWriteFailedIds, documentId),
    })),
    on(closingDocumentActions.loadDocumentsFailure, (state) => ({
        ...state,
        // REV-22366 — without a list nothing can end a row's loading state, so it ends here.
        rowWritesRunning: [],
        rowWritesAwaitingList: [],
        uploadingFilenames: [],
        uploadAwaitingList: false,
        // Rows are NOT cleared: the hub shows error with precedence anyway, and keeping them means a
        // transient failure does not make the table flash empty before it recovers.
        loading: false,
        error: true,
    })),

    // Reads the closing from the action, and that is the fix: this route is deep-linkable, so it is
    // reached cold with no hub visit before it.
    on(closingDocumentActions.loadDocument, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        documentLoading: true,
        documentError: false,
        // Opening a document starts without an old failure message.
        documentWriteFailedIds: [],
    })),
    on(closingDocumentActions.loadDocumentSuccess, (state, { document }) => ({
        ...state,
        document,
        documentLoading: false,
        documentError: false,
        // F5 (#84) — a successful write clears ITS OWN pending/failed marker, and only its own: a
        // response for `document.id` says nothing about any OTHER row's in-flight write.
        proposalPendingIds: withoutId(state.proposalPendingIds, document.id),
        proposalFailedIds: withoutId(state.proposalFailedIds, document.id),
    })),
    on(closingDocumentActions.loadDocumentFailure, (state) => ({
        ...state,
        document: null,
        documentLoading: false,
        documentError: true,
    })),
    on(closingDocumentActions.clearDocument, (state) => ({
        ...state,
        document: null,
        documentError: false,
    })),

    on(closingDocumentActions.uploadDocuments, (state, { files, accountId }) => ({
        ...state,
        uploadPending: true,
        // REV-22366 — only a hub upload: one filed on the account page never shows in the hub's Eingang.
        ...(accountId === undefined
            ? { uploadingFilenames: files.map((file) => file.name), uploadAwaitingList: false }
            : {}),
        // Failures from a PREVIOUS attempt are cleared here, not on success: otherwise the modal
        // shows last round's rejections next to this round's progress.
        uploadFailures: [],
    })),
    on(closingDocumentActions.uploadDocumentsSettled, (state, { failures, succeededFilenames }) => ({
        ...state,
        uploadPending: false,
        uploadFailures: failures,
        // A NEW watch-list, not an addition to the old one: this batch's proposals are what the poll
        // exists to wait for, and a previous batch that is still (rarely) open belongs to a chain that
        // is either already settled or will hit its own ceiling on its own terms.
        pendingUploadFilenames: succeededFilenames,
        uploadPollTick: 0,
        // REV-22366 — a failed file loses its placeholder now (the modal names it); the others wait
        // for the list that holds them.
        uploadingFilenames: state.uploadingFilenames.filter((name) => succeededFilenames.includes(name)),
        uploadAwaitingList: state.uploadingFilenames.length > 0,
    })),
    // The post-upload poll ended (#162): every watched filename left `open`, or the ceiling was
    // reached. Clearing the watch-list is what makes `pollAfterUpload$` stop scheduling further ticks.
    on(closingDocumentActions.uploadPollSettled, (state) => ({
        ...state,
        pendingUploadFilenames: [],
    })),

    // The three below are why the slice exists at all for UI state: the hub is CONTROLLED, so it
    // renders these from its `@Input()`s and never mutates them. Without the reducer answering, a
    // pill click visibly does nothing.
    // A pull can take a while (a Graph walk plus a download per file), so the flag exists to stop the
    // user firing a second one on top of the first.
    on(closingDocumentActions.syncSharepoint, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        syncPending: true,
    })),
    on(closingDocumentActions.syncSharepointSuccess, (state, { result }) => ({
        ...state,
        syncPending: false,
        lastSync: result,
    })),
    on(closingDocumentActions.syncSharepointFailure, (state) => ({ ...state, syncPending: false })),

    // --- REV-21964 - R3/R6/U1: Start, Nachfragen, Bericht ----------------------------------------
    //
    // `syncPending` traegt beide Wege. Es bedeutet weiterhin genau eine Sache - "es laeuft gerade
    // ein Abgleich" -, nur endet es beim asynchronen Weg nicht mit der Antwort auf den Start,
    // sondern mit dem Lauf. Ein zweites Flag daneben waere eine zweite Wahrheit ueber denselben
    // Knopf.
    on(closingDocumentActions.startSharepointSync, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        syncPending: true,
        // Die vorige Abweisung geht weg, BEVOR die neue Antwort da ist: sonst steht "es laeuft
        // bereits" noch ueber dem Lauf, den der Nutzer gerade selbst gestartet hat.
        syncRefusal: null,
        // Ein neuer Lauf bekommt seine eigene Decke. Sonst erbt er die Ticks des vorigen und hoert
        // je nachdem, was vorher lief, frueher oder gar nicht auf zu fragen.
        syncPollTick: 0,
    })),
    on(closingDocumentActions.startSharepointSyncResult, (state, { result }) => ({
        ...state,
        // Auch bei einer Abweisung uebernommen: `alreadyRunning` traegt die Id des LAUFENDEN Laufs,
        // und dem zu folgen ist die richtige Antwort - der Nutzer bekommt den Bericht, den er
        // wollte, statt einer Meldung ueber einen Lauf, der gerade seine Dokumente importiert.
        syncRunId: result.runId,
        syncRefusal: result.accepted ? null : result.refusal ?? null,
        // Es laeuft etwas, solange es eine Lauf-Id gibt. Nur die anderen beiden Abweisungen
        // (versiegelt, kein Ordner) lassen den Knopf sofort wieder los.
        syncPending: result.runId != null,
    })),
    on(closingDocumentActions.startSharepointSyncFailure, (state) => ({ ...state, syncPending: false })),

    // Jede Abfrage zaehlt, auch die erste: die Decke misst das Nachfragen, nicht die Wartezeit.
    on(closingDocumentActions.loadSyncRun, (state) => ({ ...state, syncPollTick: state.syncPollTick + 1 })),
    on(closingDocumentActions.loadSyncRunSuccess, (state, { run }) => ({
        ...state,
        // `null` heisst "nie synchronisiert" und ist ein Leerzustand, kein Fehler - der Bericht
        // bleibt leer und der Hub sagt es mit seinem eigenen Text.
        syncRun: run,
        syncRunId: run?.id ?? state.syncRunId,
        // Der Laufzustand ist die Quelle fuer den Spinner, nicht der Klick: faellt beim Oeffnen des
        // Reiters ein fremder, noch laufender Abgleich an, dreht sich der Knopf zu Recht.
        syncPending: run != null && (run.status === 'pending' || run.status === 'running'),
    })),
    // Der Bericht laesst sich nicht lesen. Die LISTE bleibt unberuehrt (F5) - aber der Knopf wird
    // freigegeben, sonst dreht er sich bis zum Seitenwechsel weiter und behauptet einen Lauf, ueber
    // den wir nichts mehr wissen.
    on(closingDocumentActions.loadSyncRunFailure, (state) => ({
        ...state,
        syncPending: false,
        syncPollTick: 0,
    })),
    on(closingDocumentActions.syncRunSettled, (state, { run }) => ({
        ...state,
        syncRun: run,
        syncRunId: null,
        syncPending: false,
        syncPollTick: 0,
    })),

    on(closingDocumentActions.loadAccountDocuments, (state, { projectId, accountId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        // Another account starts from nothing; a re-read of the same one keeps its list on screen.
        ...(state.accountDocumentsAccountId === accountId ? {} : { accountDocuments: [] }),
        accountDocumentsAccountId: accountId,
        accountDocumentsLoading: true,
        accountDocumentsError: false,
    })),
    on(closingDocumentActions.loadAccountDocumentsSuccess, (state, { response }) => ({
        ...state,
        accountDocuments: response.documents,
        accountDocumentsLoading: false,
        accountDocumentsError: false,
    })),
    on(closingDocumentActions.loadAccountDocumentsFailure, (state) => ({
        ...state,
        // Cleared on failure, unlike the hub's rows: an account page that kept another account's
        // documents on screen after a failed load would be showing the wrong evidence for THIS
        // account, which is worse than showing none.
        accountDocuments: [],
        accountDocumentsLoading: false,
        accountDocumentsError: true,
    })),

    // It had no handler before, so the account plan was the one thing a closing switch could not
    // clear — and it is dispatched by BOTH the hub and the deep-linkable detail route.
    on(closingDocumentActions.loadAccounts, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        accountsStatus: 'loading' as const,
    })),
    on(closingDocumentActions.loadAccountsSuccess, (state, { accounts }) => ({
        ...state,
        accounts,
        accountsStatus: 'loaded' as const,
    })),
    // A failed account load leaves the list EMPTY rather than stale: the pickers then show their
    // "no accounts" state, which is honest, instead of offering a plan that may no longer be right.
    // `'failed'` and not `'loaded'`: an empty list from a failure must be retried on the next visit,
    // while an empty list from a real answer must not.
    on(closingDocumentActions.loadAccountsFailure, (state) => ({
        ...state,
        accounts: [],
        accountsStatus: 'failed' as const,
    })),

    /**
     * The ERP sync of the ACCOUNT surface invalidates this slice's plan.
     *
     * A cross-slice listener, and the only honest place for it: the plan is the closing's chart, and
     * the chart grows when a sync creates account state documents — an event that belongs to the
     * sibling slice and has no equivalent here. Marked stale rather than refetched, so a closing whose
     * picker nobody opens costs no request.
     *
     * The SharePoint sync (`syncSharepointSuccess`) is deliberately NOT listed: it imports documents,
     * and documents do not change which accounts exist.
     */
    on(projectAccountsActions.syncSuccess, (state) => ({ ...state, accountsStatus: 'idle' as const })),

    // F5/F9/F10 (#84) — the write is now IN FLIGHT for this one document, ADDED to the set (other
    // documents already pending/failed are untouched). A fresh attempt also clears a PREVIOUS
    // failure on the SAME row only: retrying is what "der Knopf haengt nicht" means.
    on(closingDocumentActions.acceptProposal, (state, { documentId }) => ({
        ...state,
        proposalPendingIds: withId(state.proposalPendingIds, documentId),
        proposalFailedIds: withoutId(state.proposalFailedIds, documentId),
    })),
    on(closingDocumentActions.rejectProposal, (state, { documentId }) => ({
        ...state,
        proposalPendingIds: withId(state.proposalPendingIds, documentId),
        proposalFailedIds: withoutId(state.proposalFailedIds, documentId),
    })),
    // N2's write shares the SAME pending/failed sets: it is the identical "a write for this document
    // is in flight" fact, whether the write is accept/reject or the retraction.
    on(closingDocumentActions.unassignDocument, (state, { documentId }) => ({
        ...state,
        proposalPendingIds: withId(state.proposalPendingIds, documentId),
        proposalFailedIds: withoutId(state.proposalFailedIds, documentId),
    })),
    on(closingDocumentActions.proposalFailure, (state, { documentId }) => ({
        ...state,
        proposalPendingIds: withoutId(state.proposalPendingIds, documentId),
        proposalFailedIds: withId(state.proposalFailedIds, documentId),
    })),

    /**
     * F12 (#84, Sicherheitspruefung) — a pill switch is a fresh view onto the closing, and a stale
     * write-failure must not keep decorating a row the user has moved away from. NAVIGATING away
     * already clears it (`loadDocumentSuccess` above narrows to the document actually being opened);
     * `Set Filter` was the one entry point that did not, because it never touches `document` and so
     * never ran that logic. `proposalPendingIds` is deliberately LEFT ALONE here: a write genuinely
     * in flight does not stop being in flight because the user looked at a different pill.
     */
    //
    // A pill, a sort or the agent toggle is the USER asking for a different list, so the rows of the
    // previous one go and the hub shows its placeholders until the new list lands. A reload the user did
    // not ask for (the post-upload poll, the reload after a write) goes through `Load Documents` and keeps
    // its rows. The search is not here: it narrows the counts and the rows are filtered client-side.
    on(closingDocumentActions.setFilter, (state, { filter }) => ({
        ...state,
        activeFilter: filter,
        proposalFailedIds: [],
        documents: [],
    })),
    on(closingDocumentActions.setSort, (state, { sort }) => ({ ...state, sort, documents: [] })),
    on(closingDocumentActions.setSearchTerm, (state, { searchTerm }) => ({ ...state, searchTerm })),
    // REV-21901 N3 (W2) — same pattern as the three above: the hub's toggle is CONTROLLED, so the
    // reducer answering is what makes it move. Its effect reloads, like `Set Filter`'s.
    on(closingDocumentActions.setAgentOnly, (state, { agentOnly }) => ({ ...state, agentOnly, documents: [] })),
);
